import { Icon } from '@iconify/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { addDistributor } from '~features/distributors/api'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Card, CardContent, CardFooter, IconButton, Input, Label, PhoneInput, Separator, Textarea } from '~in-app/components'
import Dropdown from '~in-app/components/Dropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchCountryList } from '~in-app/shareable/index.api'
import { addressTypes, Contact, contactTypes, PersonsInCharge } from '~in-app/types'
import { distributorValidation } from '~in-app/validation/distributor.validation'

export const Route = createFileRoute('/console/distributor/add')({
  component: AddDistributorPage,
  staticData: {
    privileges: ['0.3.7.1'],
  },
})

function AddDistributorPage() {
  const router = useRouter()
  const { data: countryList = [] } = useQuery({
    queryKey: ['add-distributor-country-listing'],
    queryFn: async () => {
      const response = await fetchCountryList()
      const countryList = response?.list?.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? []
      return countryList
    },
  })
  const addNewDistributorFunc = () => {
    const mutation = useMutation({
      mutationFn: async (value: any) => {
        const response = await addDistributor(value)
        // If API returns null (both success/error cases), throw error to trigger onError
        if (!response) {
          throw new Error('Operation failed - no response received')
        }
        return response
      },
      onSuccess: (response) => {
        toast.success(`Record Added Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/distributor/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const saveNewDistributorMutation = addNewDistributorFunc()
  const submitDistributor = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))
      if (submitValue.client.personsInCharge.length === 0) {
        delete submitValue.client.personsInCharge
      }
      if (submitValue.client.contacts.length === 0) {
        delete submitValue.client.contacts
      }
      if (submitValue.client.address) {
        const address = submitValue.client.address
        const keys = Object.keys(address) as (keyof typeof address)[]
        keys.forEach((key) => {
          if (!address[key]) {
            delete address[key]
          }
        })
        if (Object.keys(address).length === 0) {
          delete submitValue.client.address
        }
      }
      saveNewDistributorMutation.mutate(submitValue)
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, { position: 'top-center', hideProgressBar: false, autoClose: 500 })
    }
  }

  const formOpts = formOptions({
    defaultValues: {
      administrator: {
        firstName: '',
        lastName: '',
        email: '',
        username: '',
      },
      client: {
        name: '',
        timezone: dayjs.tz.guess(),
        desp: '',
        remarks: '',
        address: {
          line1: '',
          line2: '',
          line3: '',
          city: '',
          state: '',
          countryId: '',
          postcode: '',
          type: '',
        },
        contacts: [] as Contact[],
        personsInCharge: [] as PersonsInCharge[],
      },
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: distributorValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks

      try {
        submitDistributor(value)
      }
      catch (error) {
        console.error('Add Distributor failed:', error)
        toast.error(`Error Submitting: ${error}`, { position: 'top-center', hideProgressBar: false, autoClose: 500 })
      }
    },
  })

  const formControl = useForm(formOpts)
  useEffect(() => {
    fetchCountryList()
  }, [])
  const [value, setValue] = useState(['item-1', 'item-2', 'item-3', 'item-4', 'item-5'])
  useEffect(() => {
    const validateFields = async () => {
      const fieldMeta = formControl.state.fieldMeta
      if (fieldMeta && formControl.state.errors.length > 0) {
        const fieldsToValidate = Object.keys(fieldMeta) as string[]
        const validationResults = await Promise.all(
          fieldsToValidate.map(async (field) => {
            const result = await formControl.validateField(field as any, 'change')
            return { field, result }
          }),
        )

        for (const { field, result: errors } of validationResults) {
          if (errors.length > 0) {
            const fieldElement = document.getElementById(field)
            fieldElement?.focus()
            // Scroll to the field element if it exists
            fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            break
          }
        }
      }
    }

    validateFields()
  }, [formControl.state.errors])

  return (
    <div>
      <div className="p-2">
        <IconButton
          color="muted"
          onClick={() => router.history.back()}
        >
          <Icon icon="lucide:arrow-left" />
        </IconButton>

        <span className="mb-4 ml-4 text-2xl font-bold">
          Add Distributor Page
        </span>
      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            noValidate
            onSubmit={
              (e) => {
                e.preventDefault()
                setValue(['item-1', 'item-2', 'item-3', 'item-4', 'item-5'])
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              <Accordion
                type="multiple"
                value={value}
                onValueChange={setValue}
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">Distributor Info</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">

                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-2">
                        <formControl.Field
                          name="client.name"
                          validators={{ onChange: distributorValidation.shape.client.shape.name }}
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="clientName"
                                  >
                                    Name
                                  </Label>

                                  <Input
                                    id="client.name"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Description Field */}
                      <div className="grid gap-2">

                        <formControl.Field name="client.timezone">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="clientTimezone"
                                  >
                                    Timezone
                                  </Label>

                                  <Dropdown
                                    id="client.timezone"
                                    label=""
                                    value={field.state.value}
                                    options={
                                      Intl.supportedValuesOf('timeZone').map(timeZone => ({
                                        label: timeZone,
                                        value: timeZone,
                                      }))
                                    }
                                    onValueChange={
                                      (e) => {
                                        field.handleChange(e)
                                        field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Description Field */}
                      <div className="grid gap-2">

                        <formControl.Field name="client.desp">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="clientDesp"
                                  >
                                    Description
                                  </Label>

                                  <Textarea
                                    id="client.desp"
                                    placeholder="Type your Description here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Remarks Field */}
                      <div className="grid gap-2">

                        <formControl.Field name="client.remarks">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="clientRemarks"
                                  >
                                    Remarks
                                  </Label>

                                  <Textarea
                                    id="client.remarks"
                                    placeholder="Type your Remarks here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">User Login Info</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">

                    {/* Administrator Fields */}
                    <div className="grid grid-cols-2 gap-2">
                      {/* First Name */}
                      <div className="grid gap-2">
                        <formControl.Field
                          name="administrator.firstName"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="admin.firstName"
                                  >
                                    First name
                                  </Label>

                                  <Input
                                    id="administrator.firstName"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Last Name */}
                      <div className="grid gap-2">

                        <formControl.Field
                          name="administrator.lastName"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="admin.lastName"
                                  >
                                    Last name
                                  </Label>

                                  <Input
                                    id="administrator.lastName"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* First Name */}
                      <div className="grid gap-2">

                        <formControl.Field
                          name="administrator.email"
                          validators={{ onChange: distributorValidation.shape.administrator.shape.email }}
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="admin.email"
                                  >
                                    Email
                                  </Label>

                                  <Input
                                    id="administrator.email"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Last Name */}
                      <div className="grid gap-2">
                        <formControl.Field
                          name="administrator.username"
                          validators={{ onChange: distributorValidation.shape.administrator.shape.username }}
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="admin.username"
                                  >
                                    Username
                                  </Label>

                                  <Input
                                    id="administrator.username"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={e => field.handleChange(e.target.value)}
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Address</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid">
                      <formControl.Field
                        name="client.address.line1"
                      >
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  required
                                  className="text-xs"
                                  htmlFor="addressLine1"
                                >
                                  Address Line 1
                                </Label>

                                <Input
                                  id="client.address.line1"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value), field.validate('change')
                                    }
                                  }
                                />

                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field
                        name="client.address.line2"
                      >
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine2"
                                >
                                  Address Line 2
                                </Label>

                                <Input
                                  id="client.address.line2"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value), field.validate('change')
                                    }
                                  }
                                />

                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field
                        name="client.address.line3"
                      >
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine3"
                                >
                                  Address Line 3
                                </Label>

                                <Input
                                  id="client.address.line3"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value), field.validate('change')
                                    }
                                  }
                                />

                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="grid">
                        <formControl.Field
                          name="client.address.postcode"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressPostcode"
                                  >
                                    Postcode
                                  </Label>

                                  <Input
                                    id="client.address.postcode"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value), field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field
                          name="client.address.city"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressCity"
                                  >
                                    City
                                  </Label>

                                  <Input
                                    id="client.address.city"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value), field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field
                          name="client.address.state"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressState"
                                  >
                                    State
                                  </Label>

                                  <Input
                                    id="client.address.state"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value), field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">

                      <div className="grid">
                        <formControl.Field
                          name="client.address.countryId"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressCountry"
                                  >
                                    Country
                                  </Label>

                                  <Dropdown
                                    id="client.address.countryId"
                                    label=""
                                    options={countryList}
                                    value={field.state.value}
                                    onValueChange={
                                      (e) => {
                                        field.handleChange(e)
                                        field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field
                          name="client.address.type"
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressType"
                                  >
                                    Type
                                  </Label>

                                  <Dropdown
                                    id="client.address.type"
                                    label=""
                                    options={addressTypes}
                                    value={field.state.value}
                                    onValueChange={
                                      (e) => {
                                        field.handleChange(e)
                                        field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Client Contact</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="client.contacts"
                    >
                      {
                        (field) => {
                          return (
                            <div className="w-full">
                              {
                                field.state.value
                                && field.state.value.length > 0 && (

                                  <div className="my-2 grid grid-cols-2 gap-2">
                                    <div className="grid gap-2">
                                      <Label
                                        className="text-xs"
                                      >
                                        Contact Type
                                      </Label>
                                    </div>

                                    <div className="grid gap-2">
                                      <Label
                                        className="text-xs"
                                      >
                                        Contact
                                      </Label>
                                    </div>
                                  </div>
                                )
                              }

                              {
                                field.state.value
                                && field.state.value.map((_, j) => {
                                  return (
                                    <div className="grid grid-cols-2 gap-2">

                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`client.contacts[${j}].type`}
                                        >
                                          {
                                            (subField) => {
                                              return (
                                                <>
                                                  <div className="relative flex flex-row items-center self-center">
                                                    <span className="mr-2">{`${j + 1}.`}</span>

                                                    <div className="relative w-full">

                                                      <Dropdown
                                                        id={`client.contacts[${j}].type`}
                                                        label=""
                                                        options={contactTypes}
                                                        value={subField.state.value}
                                                        onValueChange={
                                                          (e) => {
                                                            subField.handleChange(e)
                                                            subField.validate('change')
                                                            formControl.setFieldValue(`client.contacts[${j}].contactVal`, '')
                                                            formControl.setFieldValue(`client.contacts[${j}].contactLocalVal`, '')
                                                          }
                                                        }
                                                      />

                                                      {
                                                        subField.state.meta.errors && (
                                                          <em
                                                            className="error-message"
                                                            role="alert"
                                                          >
                                                            {subField.state.meta.errors.join(', ')}
                                                          </em>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                </>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>

                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`client.contacts[${j}].contactVal`}
                                        >
                                          {
                                            (subSubField) => {
                                              return (
                                                <>

                                                  <div className="relative w-full">
                                                    <div className="relative flex w-full flex-row items-center self-center">
                                                      {
                                                        field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                          ? (
                                                              <PhoneInput
                                                                international
                                                                className="mt-1 flex-1"
                                                                defaultCountry="MY"
                                                                id={`client.contacts[${j}].contactVal`}
                                                                value={subSubField.state.value}
                                                                onChange={
                                                                  (value: { fullNumber: string, localNumber: string }) => {
                                                                    subSubField.handleChange(value.fullNumber)
                                                                    formControl.setFieldValue(`client.contacts[${j}].contactLocalVal`, value.localNumber)

                                                                    // Validate using localNumber
                                                                    subSubField.validate('change')
                                                                  }
                                                                }
                                                              />
                                                            )
                                                          : (
                                                              <Input
                                                                className="mt-1"
                                                                disabled={field.state.value[j].type === ''}
                                                                id={`client.contacts[${j}].contactVal`}
                                                                type="text"
                                                                value={
                                                                  subSubField.state.value
                                                                }
                                                                onBlur={
                                                                  subSubField.handleBlur
                                                                }
                                                                onChange={
                                                                  (e) => {
                                                                    subSubField.handleChange(
                                                                      e.target.value,
                                                                    )
                                                                    subSubField.validate(
                                                                      'change',
                                                                    )
                                                                  }
                                                                }
                                                              />
                                                            )
                                                      }

                                                      <span className="ml-2">
                                                        <IconButton
                                                          className="rounded-full"
                                                          color="muted"
                                                          type="button"
                                                          variant="text"
                                                          onClick={
                                                            () =>
                                                              field.removeValue(j)
                                                          }
                                                        >
                                                          <Icon
                                                            color="red"
                                                            icon="lucide:trash-2"
                                                          />
                                                        </IconButton>
                                                      </span>

                                                    </div>

                                                    {
                                                      subSubField.state.meta
                                                        .errors && (
                                                        <em
                                                          className="error-message"
                                                          role="alert"
                                                        >
                                                          {
                                                            subSubField.state.meta.errors.join(
                                                              ', ',
                                                            )
                                                          }
                                                        </em>
                                                      )
                                                    }
                                                  </div>

                                                </>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>
                                    </div>
                                  )
                                })
                              }

                              <Button
                                className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                color="muted"
                                type="button"
                                onClick={() => field.pushValue({ type: '', contactVal: '', contactLocalVal: '' })}
                              >
                                Add Contact
                              </Button>
                            </div>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Person In Charge</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="client.personsInCharge"
                    >
                      {
                        (field) => {
                          return (
                            <>
                              <Button
                                className="mt-2 rounded-md border border-slate-400  bg-transparent hover:opacity-100"
                                color="muted"
                                type="button"
                                onClick={() => field.pushValue({ firstName: '', lastName: '', contacts: [] })}
                              >
                                Add person
                              </Button>

                              <div className="mt-4">

                                {
                                  field.state.value
                                  && field.state.value.map((_, i) => {
                                    return (
                                      <>

                                        <div className="gap-2">
                                          <div className="flex items-center space-x-2">
                                            <div className="text-base font-semibold">
                                              {`${i + 1}.`}
                                            </div>

                                            <div className="flex-1">
                                              <span className="ml-2 mt-4 text-base font-semibold">PIC Details</span>
                                            </div>

                                            <div className="">
                                              <IconButton
                                                className="rounded-full"
                                                color="muted"
                                                type="button"
                                                variant="text"
                                                onClick={() => field.removeValue(i)}
                                              >
                                                <Icon
                                                  color="red"
                                                  icon="lucide:trash-2"
                                                />
                                              </IconButton>
                                            </div>
                                          </div>

                                          <div className="ml-2 mt-2 w-full">
                                            <div className="grid grid-cols-2 gap-2">
                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`client.personsInCharge[${i}].firstName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              className="text-xs"
                                                              htmlFor={`clientPersonsInCharge[${i}].firstName`}
                                                            >
                                                              First Name
                                                            </Label>

                                                            <Input
                                                              id={`client.personsInCharge[${i}].firstName`}
                                                              type="text"
                                                              value={subField.state.value}
                                                              onBlur={subField.handleBlur}
                                                              onChange={e => subField.handleChange(e.target.value)}
                                                            />

                                                          </div>

                                                          {
                                                            subField.state.meta.errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {subField.state.meta.errors.join(', ')}
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>

                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`client.personsInCharge[${i}].lastName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              className="text-xs"
                                                              htmlFor={`clientPersonsInCharge[${i}].lastName`}
                                                            >
                                                              Last Name
                                                            </Label>

                                                            <Input
                                                              id={`client.personsInCharge[${i}].lastName`}
                                                              type="text"
                                                              value={subField.state.value}
                                                              onBlur={subField.handleBlur}
                                                              onChange={e => subField.handleChange(e.target.value)}
                                                            />

                                                          </div>

                                                          {
                                                            subField.state.meta.errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {subField.state.meta.errors.join(', ')}
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>
                                            </div>

                                            <span className="mt-1 text-base font-semibold">PIC Contact</span>

                                            <formControl.Field
                                              mode="array"
                                              name={`client.personsInCharge[${i}].contacts`}
                                            >
                                              {
                                                (field) => {
                                                  return (
                                                    <div className="w-full">
                                                      {
                                                        field.state.value.length > 0 && (

                                                          <div className="my-2 grid grid-cols-2 gap-2">
                                                            <div className="grid gap-2">
                                                              <Label
                                                                className="text-xs"
                                                              >
                                                                Contact Type
                                                              </Label>
                                                            </div>

                                                            <div className="grid gap-2">
                                                              <Label
                                                                className="text-xs"
                                                              >
                                                                Contact
                                                              </Label>
                                                            </div>
                                                          </div>
                                                        )
                                                      }

                                                      {
                                                        field.state.value
                                                        && field.state.value.map((_, j) => {
                                                          return (
                                                            <div className="grid grid-cols-2 gap-2">
                                                              <div className="grid gap-2">
                                                                <formControl.Field
                                                                  key={i}
                                                                  name={`client.personsInCharge[${i}].contacts[${j}].type`}
                                                                >
                                                                  {
                                                                    (subSubField) => {
                                                                      return (
                                                                        <>
                                                                          <div className="relative flex flex-row items-center self-center">
                                                                            <span className="mr-2">{`${j + 1}.`}</span>

                                                                            <div className="relative w-full">
                                                                              <Dropdown
                                                                                id={`client.personsInCharge[${i}].contacts[${j}].type`}
                                                                                label=""
                                                                                options={contactTypes}
                                                                                value={subSubField.state.value}
                                                                                onValueChange={
                                                                                  (e) => {
                                                                                    subSubField.handleChange(e)
                                                                                    formControl.setFieldValue(`client.personsInCharge[${i}].contacts[${j}].contactVal`, '')
                                                                                    formControl.setFieldValue(`client.personsInCharge[${i}].contacts[${j}].contactLocalVal`, '')
                                                                                  }
                                                                                }
                                                                              />

                                                                              {
                                                                                subSubField.state.meta.errors && (
                                                                                  <em
                                                                                    className="error-message"
                                                                                    role="alert"
                                                                                  >
                                                                                    {subSubField.state.meta.errors.join(', ')}
                                                                                  </em>
                                                                                )
                                                                              }
                                                                            </div>
                                                                          </div>
                                                                        </>
                                                                      )
                                                                    }
                                                                  }
                                                                </formControl.Field>
                                                              </div>

                                                              <div className="grid gap-2">
                                                                <formControl.Field
                                                                  key={j}
                                                                  name={`client.personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                >
                                                                  {
                                                                    (
                                                                      subSubField,
                                                                    ) => {
                                                                      return (
                                                                        <>

                                                                          <div className="relative w-full">
                                                                            <div className="relative flex w-full flex-row items-center self-center">
                                                                              {
                                                                                field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                                                  ? (
                                                                                      <PhoneInput
                                                                                        international
                                                                                        className="mt-1 flex-1"
                                                                                        defaultCountry="MY"
                                                                                        id={`client.personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                        value={subSubField.state.value}
                                                                                        onChange={
                                                                                          (value: { fullNumber: string, localNumber: string }) => {
                                                                                            subSubField.handleChange(value.fullNumber)
                                                                                            formControl.setFieldValue(`client.personsInCharge[${i}].contacts[${j}].contactLocalVal`, value.localNumber)

                                                                                            // Validate using localNumber
                                                                                            subSubField.validate('change')
                                                                                          }
                                                                                        }
                                                                                      />
                                                                                    )
                                                                                  : (
                                                                                      <Input
                                                                                        className="mt-1"
                                                                                        id={`client.personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                        type="text"
                                                                                        value={subSubField.state.value}
                                                                                        onBlur={subSubField.handleBlur}
                                                                                        onChange={e => subSubField.handleChange(e.target.value)}
                                                                                      />
                                                                                    )
                                                                              }

                                                                              <span className="ml-2">
                                                                                <IconButton
                                                                                  className="rounded-full"
                                                                                  color="muted"
                                                                                  type="button"
                                                                                  variant="text"
                                                                                  onClick={
                                                                                    () =>
                                                                                      field.removeValue(
                                                                                        j,
                                                                                      )
                                                                                  }
                                                                                >
                                                                                  <Icon
                                                                                    color="red"
                                                                                    icon="lucide:trash-2"
                                                                                  />
                                                                                </IconButton>
                                                                              </span>
                                                                            </div>

                                                                            {
                                                                              subSubField
                                                                                .state
                                                                                .meta
                                                                                .errors && (
                                                                                <em
                                                                                  className="error-message"
                                                                                  role="alert"
                                                                                >
                                                                                  {
                                                                                    subSubField.state.meta.errors.join(
                                                                                      ', ',
                                                                                    )
                                                                                  }
                                                                                </em>
                                                                              )
                                                                            }
                                                                          </div>

                                                                        </>
                                                                      )
                                                                    }
                                                                  }
                                                                </formControl.Field>
                                                              </div>
                                                            </div>
                                                          )
                                                        })
                                                      }

                                                      <Button
                                                        className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                                        color="muted"
                                                        type="button"
                                                        onClick={() => field.pushValue({ type: '', contactVal: '', contactLocalVal: '' })}
                                                      >
                                                        Add Contact
                                                      </Button>
                                                    </div>
                                                  )
                                                }
                                              }
                                            </formControl.Field>
                                          </div>

                                        </div>

                                        {
                                          field.state.value.length !== i + 1 && (
                                            <Separator className="mb-4 mt-2" />
                                          )
                                        }
                                      </>
                                    )
                                  })
                                }
                              </div>
                            </>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

            </CardContent>

            <CardFooter>
              <formControl.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
                {
                  ([canSubmit, isSubmitting]) => (
                    <Button
                      fullWidth
                      disabled={!auth$.privileges.get()?.has('0.3.7.1')}
                    >
                      {isSubmitting ? '...' : 'Save'}
                    </Button>
                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
